<template>
    <div class="prize-wheel-body" :style="{ backgroundImage: bgImageStyle }">
        <div id="header">
            <img :src="branding.mainLogo || defaultMainLogo" class="logo-main" alt="LFO Logo">
            <img :src="branding.taglineLogo || defaultTaglineLogo" class="logo-tagline" alt="More to Love">
        </div>

        <div id="main-container">
            <div id="wheel-wrapper">
                <div id="pointer-container">
                    <div id="pointer"></div>
                </div>
                <canvas ref="wheelCanvas" width="600" height="600"></canvas>
            </div>

            <button id="spin-btn" @click="spinWheel" :disabled="isSpinning">{{ spinBtnText }}</button>

            <!-- Offline Status Indicator -->
            <div v-if="isOffline || pendingSyncCount > 0" class="offline-status">
                <span v-if="isOffline" class="status-badge offline">Offline Mode</span>
                <span v-if="pendingSyncCount > 0" class="status-badge sync-pending">
                    {{ pendingSyncCount }} Win{{ pendingSyncCount > 1 ? 's' : '' }} Pending Sync
                </span>
            </div>
        </div>

        <div id="result-overlay" :class="{ active: isResultActive }"
            :style="{ display: showResultOverlay ? 'flex' : 'none' }" @click="closeResult">
            <div id="result-card" @click.stop>
                <button id="close-btn" @click="closeResult" title="Close">&times;</button>
                <p id="result-subtitle">{{ resultSubtitle }}</p>
                <h2 id="result-text">{{ resultText }}</h2>
                <button id="result-btn" class="btn-primary" @click="closeResult">{{ resultBtnText }}</button>
            </div>
        </div>

        <!-- Edit Prizes Modal -->
        <div id="edit-overlay" :class="{ active: isEditActive }" :style="{ display: showEditOverlay ? 'flex' : 'none' }"
            @click="closeEditModal">
            <div id="edit-card" @click.stop>
                <div class="modal-tabs">
                    <button class="tab-btn" :class="{ active: activeTab === 'inventory' }"
                        @click="activeTab = 'inventory'">Inventory</button>
                    <button class="tab-btn" :class="{ active: activeTab === 'stats' }"
                        @click="activeTab = 'stats'">Statistics</button>
                </div>

                <div v-if="activeTab === 'inventory'">
                    <h2 class="modal-title">Manage Prize Inventory</h2>

                    <div class="edit-table-wrapper">
                        <table class="edit-table">
                            <thead>
                                <tr>
                                    <th>Prize Name</th>
                                    <th>Weight</th>
                                    <th title="0 for unlimited">Stock</th>
                                    <th>Wins</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(seg, i) in editSegments" :key="i">
                                    <td>
                                        <input type="text" v-model="seg.name" placeholder="Prize Name" maxlength="50" />
                                    </td>
                                    <td style="width: 80px;">
                                        <input type="number" v-model.number="seg.weight" min="0" step="0.1" />
                                    </td>
                                    <td style="width: 80px;">
                                        <input type="number" v-model.number="seg.inventory" min="0" />
                                    </td>
                                    <td style="width: 80px;">
                                        <input type="number" v-model.number="seg.issued" min="0" />
                                    </td>
                                    <td style="width: 50px; text-align: center;">
                                        <button class="delete-row-btn" @click="editSegments.splice(i, 1)"
                                            :disabled="editSegments.length <= 2">&times;</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-actions-top">
                        <button class="add-row-btn"
                            @click="editSegments.push({ name: 'New Prize', weight: 1, inventory: 0, issued: 0 })">+ Add
                            New
                            Prize</button>
                    </div>

                    <div class="edit-actions">
                        <button class="btn-primary save-btn" @click="saveEdit" :disabled="isSaving">
                            <span v-if="isSaving">Saving Changes...</span>
                            <span v-else>Save All Changes</span>
                        </button>
                        <button class="btn-primary cancel-btn" @click="closeEditModal"
                            :disabled="isSaving">Cancel</button>
                    </div>
                </div>

                <div v-if="activeTab === 'stats'">
                    <h2 class="modal-title">Activity Statistics</h2>
                    <div v-if="Object.keys(dailyStats).length > 0" class="stats-section-main">
                        <div class="stats-table-wrapper">
                            <table class="stats-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Total Spins</th>
                                        <th>Total Wins</th>
                                        <th>Prizes Won</th>
                                        <th>No Wins</th>
                                        <th>Win Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="date in sortedStatsDates" :key="date">
                                        <td style="white-space: nowrap;">{{ date }}</td>
                                        <td>{{ calculateTotalSpins(dailyStats[date]) }}</td>
                                        <td>{{ calculateRealWins(dailyStats[date]) }}</td>
                                        <td>
                                            <div v-if="dailyStats[date].prizeCounts && Object.keys(filterPrizes(dailyStats[date].prizeCounts, true)).length > 0"
                                                class="prize-list">
                                                <div v-for="(count, name) in filterPrizes(dailyStats[date].prizeCounts, true)"
                                                    :key="name">
                                                    {{ count }}x {{ name }}
                                                </div>
                                            </div>
                                            <span v-else>-</span>
                                        </td>
                                        <td>
                                            <div v-if="dailyStats[date].prizeCounts && Object.keys(filterPrizes(dailyStats[date].prizeCounts, false)).length > 0"
                                                class="prize-list">
                                                <div v-for="(count, name) in filterPrizes(dailyStats[date].prizeCounts, false)"
                                                    :key="name">
                                                    {{ count }}x {{ name }}
                                                </div>
                                            </div>
                                            <span v-else>-</span>
                                        </td>
                                        <td>{{ calculateWinRate(calculateTotalSpins(dailyStats[date]),
                                            calculateRealWins(dailyStats[date])) }}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else class="no-stats-msg">
                        <p>No activity recorded yet.</p>
                    </div>
                    <div class="edit-actions" style="margin-top: 20px;">
                        <button class="btn-primary" @click="closeEditModal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue';

// Import default images to let webpack bundle them properly if missing or string
import defaultMainLogo from '@/LFO_Prize_Wheel/assets/images/VIA_Outlets_Logo_Landquart_Blue_CMYK.png';
import defaultTaglineLogo from '@/LFO_Prize_Wheel/assets/images/LOGO_MORE-TO-LOVE_EN.png';
import defaultBgImage from '@/LFO_Prize_Wheel/assets/images/MORE-TO-LOVE_Mountains.png';

const spinBtnText = ref("Tap to Spin");
const defaultPlayAgainText = ref("Play Again");
const defaultRedeemText = ref("Redeem Prize");

const defaultSegments = [
    { name: "Sorry, not a winner", type: "no_win", weight: 1 },
    { name: "Spin again", type: "spin_again", weight: 1 },
    { name: "Instant prize", type: "instant_prize", weight: 1 },
    { name: "Voucher", type: "voucher", weight: 1 },
    { name: "Sorry, not a winner", type: "no_win", weight: 1 },
    { name: "Spin again", type: "spin_again", weight: 1 },
    { name: "Instant prize", type: "instant_prize", weight: 1 },
    { name: "Voucher", type: "voucher", weight: 1 }
];

const defaultBranding = {
    mainLogo: defaultMainLogo,
    taglineLogo: defaultTaglineLogo,
    bgImage: defaultBgImage,
    colorA: "#ffffff",
    colorB: "#17233D",
    textA: "#17233D",
    textB: "#ffffff",
    hubText: "LFO"
};

const rawPrizes = ref([]);
const segments = ref([]);
const branding = ref({ ...defaultBranding });

const wheelCanvas = ref(null);
let ctx = null;

let currentRotation = 0;
let rotation = 0;
let isSpinning = ref(false);

const showResultOverlay = ref(false);
const isResultActive = ref(false);
const resultSubtitle = ref("");
const resultText = ref("");
const resultBtnText = ref("");
const chosenWinningIndex = ref(null);

const showEditOverlay = ref(false);
const isEditActive = ref(false);
const isSaving = ref(false);
const editSegments = ref([]);
const activeTab = ref('inventory');
const wheelId = ref(null);
const dailyStats = ref({});
const isOffline = ref(!navigator.onLine);
const pendingSyncCount = ref(0);

let reqFrame = null;
const { proxy: globalProxy } = getCurrentInstance();

const bgImageStyle = computed(() => {
    return branding.value.bgImage ? `url('${branding.value.bgImage}')` : 'none';
});

const sortedStatsDates = computed(() => {
    return Object.keys(dailyStats.value).sort((a, b) => new Date(b) - new Date(a));
});

function calculateWinRate(spins, wins) {
    if (!spins) return 0;
    return ((wins / spins) * 100).toFixed(1);
}

function calculateRealWins(stats) {
    if (!stats || !stats.prizeCounts) return (stats && stats.wins) || 0;
    let realWins = 0;
    for (const [prizeName, count] of Object.entries(stats.prizeCounts)) {
        const lower = prizeName.toLowerCase();
        if (lower.includes('nochmal') ||
            lower.includes('leider kein') ||
            lower.includes('no win') ||
            lower.includes('sorry') ||
            lower.includes('not a winner') ||
            lower.includes('spin again')) {
            continue;
        }
        realWins += count;
    }
    return realWins;
}

function calculateTotalSpins(stats) {
    if (!stats) return 0;
    const recordedSpins = Number(stats.spins) || 0;
    if (!stats.prizeCounts) return recordedSpins;
    const prizeTotal = Object.values(stats.prizeCounts).reduce((a, b) => a + (Number(b) || 0), 0);
    return Math.max(recordedSpins, prizeTotal);
}

function filterPrizes(prizeCounts, isRealWinTarget) {
    if (!prizeCounts) return {};
    const result = {};
    for (const [name, count] of Object.entries(prizeCounts)) {
        const lower = name.toLowerCase();
        const isNonWin = lower.includes('nochmal') ||
            lower.includes('leider kein') ||
            lower.includes('no win') ||
            lower.includes('sorry') ||
            lower.includes('not a winner') ||
            lower.includes('spin again');

        if ((isRealWinTarget && !isNonWin) || (!isRealWinTarget && isNonWin)) {
            result[name] = count;
        }
    }
    return result;
}

const colors = computed(() => [branding.value.colorA || defaultBranding.colorA, branding.value.colorB || defaultBranding.colorB]);
const textColors = computed(() => [branding.value.textA || defaultBranding.textA, branding.value.textB || defaultBranding.textB]);

onMounted(() => {
    if (wheelCanvas.value) {
        ctx = wheelCanvas.value.getContext("2d");
        wheelCanvas.value.addEventListener('click', handleWheelClick);
        init();
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    updatePendingSyncCount();
});

watch(activeTab, (newTab) => {
    if (newTab === 'stats') {
        fetchWheelContent(globalProxy);
    }
});

onUnmounted(() => {
    if (reqFrame) cancelAnimationFrame(reqFrame);
    if (wheelCanvas.value) {
        wheelCanvas.value.removeEventListener('click', handleWheelClick);
    }
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
});

function handleOnline() {
    isOffline.value = false;
    syncPendingWins();
}

function handleOffline() {
    isOffline.value = true;
}

function updatePendingSyncCount() {
    const pending = JSON.parse(localStorage.getItem('wheel_pending_wins') || '[]');
    pendingSyncCount.value = pending.length;
}

async function syncPendingWins() {
    if (isOffline.value || !wheelId.value) return;

    const pending = JSON.parse(localStorage.getItem('wheel_pending_wins') || '[]');
    if (pending.length === 0) return;

    console.log(`Syncing ${pending.length} pending wins...`);

    const remaining = [];
    for (const win of pending) {
        try {
            await globalProxy.$xhr.post(`/api/wheel-contents/${wheelId.value}/win`, {
                prizeName: win.prizeName,
                index: win.index
            });
        } catch (e) {
            console.error("Failed to sync win, keeping in queue", e);
            remaining.push(win);
        }
    }

    localStorage.setItem('wheel_pending_wins', JSON.stringify(remaining));
    updatePendingSyncCount();

    if (remaining.length === 0) {
        // Refresh data to get latest backend state
        fetchWheelContent(globalProxy);
    }
}

function handleWheelClick(event) {
    if (isSpinning.value) return;

    const canvas = wheelCanvas.value;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Get click coords relative to canvas center
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // Calculate distance from center
    const dist = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(y - cy, 2));

    // Hub radius is 40. Allow a small buffer (50px total)
    if (dist <= 50) {
        openEditModal();
    }
}

async function fetchWheelContent(proxy) {
    try {
        const response = await proxy.$xhr.get('/api/wheel-contents');
        if (response.data && response.data.data && response.data.data.length > 0) {
            const entry = response.data.data[0];
            wheelId.value = entry.id;
            const attrs = entry.attributes;

            if (attrs.prizes && Array.isArray(attrs.prizes)) {
                rawPrizes.value = JSON.parse(JSON.stringify(attrs.prizes));
                // Cache for offline use
                localStorage.setItem('wheel_cache_prizes', JSON.stringify(rawPrizes.value));
                localStorage.setItem('wheel_cache_id', wheelId.value);
            } else {
                rawPrizes.value = JSON.parse(JSON.stringify(defaultSegments));
            }

            if (attrs.dailyStats) {
                dailyStats.value = attrs.dailyStats;
            }
        } else {
            rawPrizes.value = JSON.parse(JSON.stringify(defaultSegments));
        }
    } catch (error) {
        console.warn("Failed to fetch wheel content from Strapi collection, trying cache", error);
        const cached = localStorage.getItem('wheel_cache_prizes');
        const cachedId = localStorage.getItem('wheel_cache_id');
        if (cached) {
            rawPrizes.value = JSON.parse(cached);
            wheelId.value = cachedId;
        } else {
            rawPrizes.value = JSON.parse(JSON.stringify(defaultSegments));
        }
    }
    // All prizes are shown on the wheel
    segments.value = JSON.parse(JSON.stringify(rawPrizes.value));

    // Apply pending local syncs to the current view so we don't over-draw
    const pending = JSON.parse(localStorage.getItem('wheel_pending_wins') || '[]');
    pending.forEach(win => {
        if (segments.value[win.index]) {
            segments.value[win.index].issued = (Number(segments.value[win.index].issued) || 0) + 1;
        }
        if (rawPrizes.value[win.index]) {
            rawPrizes.value[win.index].issued = (Number(rawPrizes.value[win.index].issued) || 0) + 1;
        }
    });

    if (!isOffline.value) {
        syncPendingWins();
    }
}

async function init() {
    const { proxy } = getCurrentInstance();
    await fetchWheelContent(proxy);
    drawWheel();
    reqFrame = requestAnimationFrame(mainLoop);
}

function drawWheel() {
    if (!ctx) return;
    const canvas = wheelCanvas.value;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const radius = canvas.width / 2 - 10;
    const numSegments = segments.value.length;
    const sliceAngle = (2 * Math.PI) / numSegments;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw Slices
    for (let i = 0; i < numSegments; i++) {
        const s = segments.value[i];
        const angle = i * sliceAngle + rotation;

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, radius, angle, angle + sliceAngle);
        ctx.closePath();

        ctx.fillStyle = colors.value[i % 2];
        ctx.fill();

        ctx.strokeStyle = "#17233D";
        ctx.lineWidth = 2;
        ctx.stroke();

        // Add Text
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(angle + sliceAngle / 2);
        ctx.textAlign = "right";
        ctx.fillStyle = textColors.value[i % 2];
        ctx.font = "bold 20px Gotham";

        const label = s.name;
        ctx.fillText(label, radius - 30, 8);
        ctx.restore();
    }

    // Center Decorative Hub
    ctx.beginPath();
    ctx.arc(cx, cy, 40, 0, 2 * Math.PI);
    ctx.fillStyle = "#17233D";
    ctx.fill();
    ctx.strokeStyle = "#17233D";
    ctx.lineWidth = 4;
    ctx.stroke();

    // Innerhub logo text
    ctx.fillStyle = branding.value.textB;
    ctx.font = "bold 12px Adieu";
    ctx.textAlign = "center";
    ctx.fillText(branding.value.hubText || "LFO", cx, cy + 5);
}

let targetRotation = 0;
let spinStartRotation = 0;
let spinStartTime = 0;
const spinDuration = 7000;
let lastTime = 0;

function mainLoop(now) {
    if (!lastTime) lastTime = now;
    const dt = (now - lastTime) / 1000;
    lastTime = now;

    if (isSpinning.value) {
        const elapsed = now - spinStartTime;
        const t = Math.min(elapsed / spinDuration, 1);

        const L = targetRotation - spinStartRotation;
        const D = spinDuration / 1000;
        const vi = (0.5 * D) / L;

        const s = (Math.pow(t, 3) - 2 * Math.pow(t, 2) + t) * vi + (-2 * Math.pow(t, 3) + 3 * Math.pow(t, 2));

        rotation = spinStartRotation + L * s;

        if (t >= 1) {
            stopSpin();
        }
    } else if (!showResultOverlay.value) {
        rotation += 0.5 * dt;
    }

    drawWheel();
    reqFrame = requestAnimationFrame(mainLoop);
}

function spinWheel() {
    if (isSpinning.value) return;

    // Filter available prizes
    const availablePrizes = segments.value
        .map((s, i) => ({ ...s, originalIndex: i }))
        .filter(s => {
            const inv = Number(s.inventory);
            const iss = Number(s.issued || 0);
            if (isNaN(inv) || inv <= 0) return true;
            return iss < inv;
        });

    if (availablePrizes.length === 0) {
        alert("Sorry, all prizes are currently out of stock!");
        return;
    }

    const totalWeight = availablePrizes.reduce((sum, s) => sum + (s.weight || 0), 0);
    let random = Math.random() * totalWeight;
    let winningIndex = availablePrizes[0].originalIndex;

    for (const p of availablePrizes) {
        const w = p.weight || 0;
        if (random < w) {
            winningIndex = p.originalIndex;
            break;
        }
        random -= w;
    }

    const numSegments = segments.value.length;
    const sliceAngle = (2 * Math.PI) / numSegments;
    const pointerAngle = 1.5 * Math.PI;

    const randomOffset = 0.3 + Math.random() * 0.4;
    const targetInRotation = (pointerAngle - (winningIndex + randomOffset) * sliceAngle + 10 * Math.PI) % (2 * Math.PI);

    const extraSpins = 6 + Math.floor(Math.random() * 2);

    spinStartRotation = rotation;
    const currentNormalized = spinStartRotation % (2 * Math.PI);
    const dist = (targetInRotation - currentNormalized + 2 * Math.PI) % (2 * Math.PI);
    targetRotation = rotation + dist + (extraSpins * 2 * Math.PI);

    spinStartTime = performance.now();
    chosenWinningIndex.value = winningIndex;
    isSpinning.value = true;

    // Record spin with backend
    if (wheelId.value && !isOffline.value) {
        globalProxy.$xhr.post(`/api/wheel-contents/${wheelId.value}/spin`).catch(e => {
            console.error("Failed to record spin", e);
        });
    }
}

function stopSpin() {
    isSpinning.value = false;

    if (chosenWinningIndex.value !== null) {
        showResult(segments.value[chosenWinningIndex.value].name, chosenWinningIndex.value);
    } else {
        // Fallback to physics if for some reason chosenWinningIndex is missing
        const numSegments = segments.value.length;
        const sliceAngle = (2 * Math.PI) / numSegments;
        const pointerAngle = 1.5 * Math.PI;
        let normalizedRotation = rotation % (2 * Math.PI);
        if (normalizedRotation < 0) normalizedRotation += 2 * Math.PI;

        let localPointerAngle = (pointerAngle - normalizedRotation);
        while (localPointerAngle < 0) localPointerAngle += 2 * Math.PI;
        localPointerAngle %= (2 * Math.PI);

        let winningIndex = Math.floor(localPointerAngle / sliceAngle);
        winningIndex = winningIndex % numSegments;

        showResult(segments.value[winningIndex].name, winningIndex);
    }
}

async function showResult(prize, index) {
    const prizeLower = prize.toLowerCase();
    const isNoWin = prizeLower.includes("no win") || prizeLower.includes("sorry") || prizeLower.includes("not a winner");
    const isSpinAgain = prizeLower.includes("spin again");

    if (isNoWin) {
        resultSubtitle.value = "Maybe next time...";
        resultBtnText.value = "Try Again";
    } else if (isSpinAgain) {
        resultSubtitle.value = "One more chance!";
        resultBtnText.value = "Spin Again!";
    } else {
        resultSubtitle.value = "Congratulations!";
        resultBtnText.value = "Redeem Prize";

        // Update local rawPrizes and segments issued count by index
        // This is more reliable than finding by name if there are duplicates
        if (segments.value[index]) {
            segments.value[index].issued = (Number(segments.value[index].issued) || 0) + 1;
        }
        if (rawPrizes.value[index]) {
            rawPrizes.value[index].issued = (Number(rawPrizes.value[index].issued) || 0) + 1;
        }

        // Sync win with backend if we have a wheelId
        if (wheelId.value) {
            try {
                await globalProxy.$xhr.post(`/api/wheel-contents/${wheelId.value}/win`, {
                    prizeName: prize,
                    index: index
                });
            } catch (e) {
                console.error("Failed to sync win with backend, queueing for later", e);
                const pending = JSON.parse(localStorage.getItem('wheel_pending_wins') || '[]');
                pending.push({ prizeName: prize, index: index, timestamp: Date.now() });
                localStorage.setItem('wheel_pending_wins', JSON.stringify(pending));
                updatePendingSyncCount();
            }
        }
    }

    resultText.value = prize;
    showResultOverlay.value = true;
    setTimeout(() => {
        isResultActive.value = true;
    }, 10);
}

function closeResult() {
    isResultActive.value = false;
    setTimeout(() => {
        showResultOverlay.value = false;
    }, 500);
}

function openEditModal() {
    activeTab.value = 'inventory';
    editSegments.value = JSON.parse(JSON.stringify(rawPrizes.value));
    showEditOverlay.value = true;
    setTimeout(() => {
        isEditActive.value = true;
    }, 10);
}

function closeEditModal() {
    isEditActive.value = false;
    setTimeout(() => {
        showEditOverlay.value = false;
    }, 500);
}

async function saveEdit() {
    if (!globalProxy) return;

    isSaving.value = true;
    try {
        const payload = {
            data: {
                wheelName: "Main Wheel", // Default name for the entry
                prizes: editSegments.value.map(s => ({
                    name: s.name,
                    weight: Number(s.weight) || 0,
                    inventory: Number(s.inventory) || 0,
                    issued: Number(s.issued) || 0
                }))
            }
        };

        if (wheelId.value) {
            // Update existing
            await globalProxy.$xhr.put(`/api/wheel-contents/${wheelId.value}`, payload);
        } else {
            // Create first entry if none exists
            const res = await globalProxy.$xhr.post('/api/wheel-contents', payload);
            if (res.data && res.data.data) {
                wheelId.value = res.data.data.id;
            }
        }

        // Update local state
        rawPrizes.value = JSON.parse(JSON.stringify(payload.data.prizes));
        segments.value = JSON.parse(JSON.stringify(rawPrizes.value));

        // Update cache
        localStorage.setItem('wheel_cache_prizes', JSON.stringify(rawPrizes.value));
        localStorage.setItem('wheel_cache_id', wheelId.value);

        drawWheel();
        closeEditModal();
    } catch (e) {
        console.error("Failed to save prizes to Strapi Collection", e);
        alert("Failed to save prizes. Check console or permissions.");
    } finally {
        isSaving.value = false;
    }
}
</script>

<style scoped>
@font-face {
    font-family: 'Adieu';
    src: url('~@/LFO_Prize_Wheel/assets/fonts/Adieu-Regular.ttf') format('truetype');
    font-weight: normal;
}

@font-face {
    font-family: 'Adieu';
    src: url('~@/LFO_Prize_Wheel/assets/fonts/adieu-bold.otf') format('opentype');
    font-weight: bold;
}

@font-face {
    font-family: 'Gotham';
    src: url('~@/LFO_Prize_Wheel/assets/fonts/GothamBook.ttf') format('truetype');
    font-weight: normal;
}

@font-face {
    font-family: 'Gotham';
    src: url('~@/LFO_Prize_Wheel/assets/fonts/Gotham-Medium.otf') format('opentype');
    font-weight: 500;
}

.prize-wheel-body {
    --lfo-navy: #17233D;
    --lfo-gold: #17233D;
    --lfo-white: #ffffff;
    --text-primary: #17233D;
    --bg-color: #f8f9fa;

    margin: 0;
    padding: 0;
    font-family: 'Gotham', sans-serif;
    background-color: var(--bg-color);
    background-position: bottom center;
    background-repeat: no-repeat;
    background-size: 100% auto;
    min-height: 100vh;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    color: var(--lfo-navy);
}

.prize-wheel-body * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
}

#header {
    width: 100%;
    padding: 30px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 10;
}

.logo-main {
    height: 60px;
}

.logo-tagline {
    height: 30px;
}

#main-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    position: relative;
    z-index: 5;
    margin-top: -50px;
}

#wheel-wrapper {
    position: relative;
    width: 600px;
    height: 600px;
    filter: drop-shadow(0 20px 40px rgba(23, 35, 61, 0.2));
}

canvas {
    width: 100%;
    height: 100%;
    border-radius: 50%;
}

/* The Pointer / Indicator */
#pointer-container {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
}

#pointer {
    width: 40px;
    height: 50px;
    background-color: #ffffff;
    clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
    border-bottom: 3px solid var(--lfo-navy);
}

#spin-btn {
    margin-top: 50px;
    padding: 18px 60px;
    font-family: 'Adieu', sans-serif;
    font-size: 24px;
    font-weight: bold;
    background-color: var(--lfo-navy);
    color: white;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    box-shadow: 0 10px 25px rgba(23, 35, 61, 0.3);
    text-transform: uppercase;
    letter-spacing: 2px;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

#spin-btn:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 35px rgba(23, 35, 61, 0.4);
}

#spin-btn:active {
    transform: translateY(1px) scale(0.98);
}

#spin-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

/* Result Display - Premium Glassmorphism */
#result-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(23, 35, 61, 0.4);
    backdrop-filter: blur(10px);
    display: none;
    justify-content: center;
    align-items: center;
    z-index: 100;
}

#result-card {
    background: white;
    padding: 60px;
    border-radius: 20px;
    text-align: center;
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
    max-width: 500px;
    width: 90%;
    transform: scale(0.8);
    opacity: 0;
    position: relative;
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

#close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 40px;
    color: var(--lfo-navy);
    cursor: pointer;
    line-height: 1;
    padding: 5px 10px;
    transition: transform 0.2s;
}

#close-btn:hover {
    transform: scale(1.1);
}

#result-overlay.active #result-card {
    transform: scale(1);
    opacity: 1;
}

#result-card h2 {
    font-family: 'Adieu', sans-serif;
    font-size: 48px;
    margin: 0 0 15px 0;
    color: var(--lfo-navy);
}

#result-card p {
    font-size: 20px;
    margin-bottom: 30px;
    color: #666;
}

.btn-primary {
    padding: 15px 40px;
    background-color: var(--lfo-navy);
    color: white;
    border: none;
    border-radius: 30px;
    font-family: 'Adieu', sans-serif;
    font-size: 18px;
    cursor: pointer;
    text-transform: uppercase;
}

.edit-prizes-btn {
    background-color: transparent;
    border: 2px solid var(--lfo-navy);
    color: var(--lfo-navy);
    padding: 10px 20px;
    border-radius: 20px;
    font-family: 'Gotham', sans-serif;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
}

.edit-prizes-btn:hover {
    background-color: var(--lfo-navy);
    color: white;
}

/* Offline Status */
.offline-status {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.status-badge.offline {
    background-color: #ff4444;
    color: white;
}

.status-badge.sync-pending {
    background-color: #ff9800;
    color: white;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }

    100% {
        opacity: 1;
    }
}

#edit-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(23, 35, 61, 0.6);
    backdrop-filter: blur(5px);
    display: none;
    justify-content: center;
    align-items: center;
    z-index: 200;
}

#edit-card {
    background: white;
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    max-width: 1100px;
    width: 98%;
    transform: scale(0.8);
    opacity: 0;
    transition: all 0.3s ease;
    text-align: left;
}

#edit-overlay.active #edit-card {
    transform: scale(1);
    opacity: 1;
}

.modal-title {
    font-family: 'Adieu';
    color: var(--lfo-navy);
    margin-bottom: 25px;
    text-align: center;
    font-size: 24px;
}

.edit-table-wrapper {
    max-height: 50vh;
    overflow-y: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;
    border-radius: 8px;
}

.edit-table {
    width: 100%;
    border-collapse: collapse;
}

.edit-table th {
    background: #f8f9fa;
    padding: 12px;
    text-align: left;
    font-weight: bold;
    color: var(--lfo-navy);
    border-bottom: 2px solid #eee;
    font-size: 14px;
}

.edit-table td {
    padding: 8px;
    border-bottom: 1px solid #eee;
}

.edit-table input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.edit-table input:focus {
    border-color: var(--lfo-navy);
    outline: none;
}

.delete-row-btn {
    background: #ff4444;
    color: white;
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
}

.delete-row-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.modal-actions-top {
    margin-bottom: 30px;
}

.add-row-btn {
    width: 100%;
    padding: 12px;
    background: #4CAF50;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    font-family: 'Adieu';
}

.edit-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.save-btn {
    flex: 2;
}

.cancel-btn {
    flex: 1;
    background-color: #666;
}

.stats-section-main {
    margin-top: 10px;
}

.modal-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
}

.tab-btn {
    padding: 10px 25px;
    border: none;
    background: #f1f1f1;
    color: #666;
    border-radius: 8px;
    cursor: pointer;
    font-family: 'Gotham';
    font-weight: bold;
    transition: all 0.2s;
}

.tab-btn.active {
    background: var(--lfo-navy);
    color: white;
}

.no-stats-msg {
    text-align: center;
    padding: 40px;
    color: #999;
}

.stats-table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 8px;
}

.stats-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}

.stats-table th {
    background: #f8f9fa;
    padding: 12px;
    text-align: left;
    border-bottom: 2px solid #eee;
    position: sticky;
    top: 0;
    white-space: nowrap;
}

.stats-table td {
    padding: 12px;
    border-bottom: 1px solid #eee;
}

.prize-list {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
}

@media (max-width: 600px) {
    #navbar {
        padding: 10px 20px;
    }

    .edit-table-wrapper {
        max-height: 40vh;
    }

    .edit-table th,
    .edit-table td {
        padding: 6px;
        font-size: 12px;
    }
}
</style>
